import { jsxs, jsx } from 'react/jsx-runtime';
import { useCallback, useLayoutEffect, useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { CircleExclamationIcon } from '../../../icons/CircleExclamationIcon.js';
import { CircleTickIcon } from '../../../icons/CircleTickIcon.js';
import { useListboxDropdown } from '../../../hooks/useListboxDropdown/useListboxDropdown.js';

const styles = {
    dropdown: 'fixed py-space-200 px-space-400 bg-neutral-0 shadow-[0_6px_16px_0px_rgba(0,0,0,0.12)] z-999',
    expandButton: 'flex justify-between items-center  border b-1 cursor-pointer w-full h-12 px-space-400 py-space-200 bg-input-background-default rounded-radius-100 disabled:bg-input-background-disabled  border-input-border-default disabled:border-input-border-disabled focus:border-border-focus-2 focus:border-3 focus:outline-none ',
    buttonError: 'border-input-border-error',
    buttonSuccess: 'border-input-border-success',
    option: 'bg-neutral-0  text-neutral-900 cursor-pointer px-space-400 py-space-200  rounded-tenant hover:bg-primary-100 whitespace-nowrap overflow-hidden truncate focus:outline-none focus:bg-primary-100',
    container: 'relative w-full',
    label: 'relative w-full block mb-space-200 text-body-strong',
    placeholder: 'text-neutral-400',
    hintTextContainer: 'mt-space-200 flex gap-space-100',
    errorText: 'text-body-small text-(--text-error-color)',
    successText: 'text-body-small text-(--text-success-color)',
    hintIconContainer: 'flex items-center h-(--line-height-body-small)',
};
const Select = (props) => {
    const ChevronDown = (isOpen) => (jsx("svg", { className: `transition-transform duration-200 ${isOpen ? 'rotate-180' : 'rotate-0'}`, width: "14", height: "8", viewBox: "0 0 14 8", fill: "none", xmlns: "http://www.w3.org/2000/svg", "aria-hidden": "true", focusable: "false", children: jsx("path", { d: "M6.90625 6.90625C6.71875 7.09375 6.375 7.09375 6.1875 6.90625L0.1875 0.90625C0 0.71875 0 0.375 0.1875 0.1875C0.375 0 0.71875 0 0.90625 0.1875L6.5625 5.84375L12.1875 0.1875C12.375 0 12.7188 0 12.9062 0.1875C13.0938 0.375 13.0938 0.71875 12.9062 0.90625L6.90625 6.90625Z", fill: "currentColor" }) }));
    const { id, options, placeholder, onChange, onBlur, value, maxHeight, label, hasError, hasSuccess, } = props;
    const handleSelect = (optionValue) => onChange(optionValue);
    const { isOpen, activeIndex, containerRef, triggerRef, listRef, toggle, selectIndex, handleTriggerKeyDown, handleOptionKeyDown, registerOptionRef, } = useListboxDropdown({
        itemCount: options.length,
        selectedIndex: options.findIndex((option) => option.value === value),
        getItemText: (index) => options[index]?.label ?? '',
        onSelect: (index) => {
            const option = options[index];
            if (option)
                handleSelect(option.value);
        },
    });
    // Positions the dropdown to align with the button using fixed coordinates.
    // Direct DOM mutation runs synchronously to avoid a visible layout shift.
    const updatePosition = useCallback(() => {
        if (!triggerRef.current || !listRef.current)
            return;
        const rect = triggerRef.current.getBoundingClientRect();
        const list = listRef.current;
        list.style.top = `${rect.bottom + 4}px`;
        list.style.left = `${rect.left}px`;
        list.style.width = `${rect.width}px`;
        list.style.maxHeight = maxHeight ? `${maxHeight}px` : '';
        list.style.overflowY = maxHeight ? 'auto' : '';
    }, [maxHeight, listRef, triggerRef]);
    const textToDisplay = value !== undefined && value !== ''
        ? (options.find((option) => option.value === value)?.label ?? placeholder)
        : placeholder;
    // Synchronously show/hide the portal dropdown and update its position before paint.
    // useLayoutEffect fires before the browser paints so there is no visible flash.
    useLayoutEffect(() => {
        const list = listRef.current;
        if (!list)
            return;
        if (isOpen) {
            updatePosition();
            list.style.display = '';
        }
        else {
            list.style.display = 'none';
        }
    }, [isOpen, listRef, updatePosition]);
    useEffect(() => {
        if (!isOpen)
            return;
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);
        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [isOpen, updatePosition]);
    const generatedId = useId();
    const baseId = id ?? generatedId;
    const buttonId = `${baseId}-button`;
    const labelId = `${baseId}-label`;
    const errorId = `${baseId}-error`;
    const successId = `${baseId}-success`;
    return (jsxs("div", { ref: containerRef, className: styles.container, id: id, children: [label && (jsx("label", { id: labelId, htmlFor: buttonId, className: styles.label, children: label })), jsxs("button", { id: buttonId, type: "button", ref: triggerRef, onClick: () => toggle(), onKeyDown: handleTriggerKeyDown, onBlur: (event) => {
                    const related = event.relatedTarget;
                    const stillInsideWidget = containerRef.current?.contains(related) || listRef.current?.contains(related);
                    if (!stillInsideWidget) {
                        onBlur?.(value ?? '');
                    }
                }, className: `${styles.expandButton} ${hasError ? styles.buttonError : ''} ${hasSuccess && !hasError ? styles.buttonSuccess : ''}`, "aria-expanded": isOpen, "aria-haspopup": "listbox", "aria-controls": `${baseId}-listbox`, children: [jsx("span", { className: !value ? styles.placeholder : '', children: textToDisplay }), ChevronDown(isOpen)] }), createPortal(jsx("ul", { id: `${baseId}-listbox`, ref: listRef, className: styles.dropdown, style: { display: 'none' }, role: "listbox", "aria-labelledby": label ? labelId : undefined, children: options.map((option, index) => (jsx("li", { role: "option", ref: registerOptionRef(index), tabIndex: index === activeIndex ? 0 : -1, onClick: () => selectIndex(index), onKeyDown: (event) => handleOptionKeyDown(event, index), className: styles.option, "aria-selected": value === option.value, children: option.label }, option.value))) }), document.body), hasError && typeof hasError === 'string' && (jsxs("div", { className: `${styles.hintTextContainer} text-text-error`, children: [jsx("div", { className: styles.hintIconContainer, children: jsx(CircleExclamationIcon, {}) }), jsx("p", { id: errorId, className: styles.errorText, role: "alert", children: hasError })] })), hasSuccess && !hasError && typeof hasSuccess === 'string' && (jsxs("div", { className: `${styles.hintTextContainer} text-text-success`, children: [jsx("div", { className: styles.hintIconContainer, children: jsx(CircleTickIcon, {}) }), jsx("p", { id: successId, className: styles.successText, children: hasSuccess })] }))] }));
};

export { Select };
